import * as React from 'react';
import { ThemeProvider, createTheme, GlobalStyles } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';
import { colorSchemes, shape, custom, breakpoints } from './themePrimitives';
import { responsiveFontSize } from '../util/responsiveUtils';
import { ThemeColorMeta } from '../components/ThemeColorMeta';
import { PageMeta } from '../components/PageMeta';

// Augment the Theme type to include custom
declare module '@mui/material/styles' {
  interface Theme {
    custom: typeof custom;
  }
  interface ThemeOptions {
    custom?: typeof custom;
  }
  interface Palette {
    teal: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    redOrange: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    teal?: {
      light?: string;
      main?: string;
      dark?: string;
      contrastText?: string;
    };
    redOrange?: {
      light?: string;
      main?: string;
      dark?: string;
      contrastText?: string;
    };
  }
}

interface AppThemeProps {
    children: React.ReactNode;
    /**
     * This is for the docs site. You can ignore it or remove it.
     */
    disableCustomTheme?: boolean;
    themeComponents?: ThemeOptions['components'];
}

export default function AppTheme(props: AppThemeProps) {
    const { children, disableCustomTheme, themeComponents } = props;
    const theme = React.useMemo(() => {
        return disableCustomTheme
            ? {}
            : createTheme({
                cssVariables: {
                    colorSchemeSelector: 'data-mui-color-scheme',
                    cssVarPrefix: 'template',
                },
                colorSchemes,
                shape,
                breakpoints: {
                    values: breakpoints,
                },
                // Add custom to the theme
                custom,
                components: {
                    MuiContainer: {
                        styleOverrides: {
                            root: {
                                paddingLeft: {
                                    xs: 16,
                                    sm: 24,
                                    md: 32,
                                },
                                paddingRight: {
                                    xs: 16,
                                    sm: 24,
                                    md: 32,
                                },
                            } as any,
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                textTransform: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: responsiveFontSize.buttonRoot,
                                padding: {
                                    xs: '8px 16px',
                                    sm: '10px 20px',
                                    md: '12px 24px',
                                },
                            } as any,
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 8,
                                },
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                            },
                        },
                    },
                    MuiCircularProgress: {
                        styleOverrides: {
                            root: {
                                // Use css variable for palette text primary so spinner matches light/dark mode
                                color: 'var(--template-palette-text-primary)'
                            }
                        }
                    },
                    // Prevent layout shift when opening/closing any modal (Dialog, Drawer, Menu, Popover).
                    // Without this, body gets overflow:hidden + padding-right, so the scrollbar disappears and content shifts.
                    MuiModal: {
                        defaultProps: {
                            disableScrollLock: true,
                        },
                    },
                    // Menu uses Popover/Modal; ensure disableScrollLock is set at Menu level so it isn't overridden.
                    MuiMenu: {
                        defaultProps: {
                            disableScrollLock: true,
                        },
                    },
                    MuiPopover: {
                        defaultProps: {
                            disableScrollLock: true,
                        },
                    },
                    ...themeComponents,
                },
            });
    }, [disableCustomTheme, themeComponents]);
    if (disableCustomTheme) {
        return <React.Fragment>{children}</React.Fragment>;
    }
    return (
        <ThemeProvider theme={theme} disableTransitionOnChange {...({ forceThemeRerender: true } as any)}>
            <GlobalStyles
                styles={{
                    html: {
                        scrollbarGutter: 'stable',
                    },
                }}
            />
            <ThemeColorMeta />
            <PageMeta />
            {children}
        </ThemeProvider>
    );
}
